'use client'

import { useState } from 'react'
import { useLocale } from 'next-intl'
import { getInstrumentContent, type FaqItem, type InstrumentKind } from '@/data/instrumentContent'
import { t } from '@/i18n/translations'
import { faqSchema, safeJsonLd } from '@/lib/seo'

function FaqAccordion({ items, lang }: { items: FaqItem[]; lang: string }) {
  const [open, setOpen] = useState<number | null>(null)

  if (items.length === 0) return null

  return (
    <div className="mt-6">
      <h3
        className="text-[15px] font-bold mb-3"
        style={{ color: 'var(--text-main)' }}
      >
        {t('instrument.faqTitle', lang)}
      </h3>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div
            key={i}
            className="rounded-lg border overflow-hidden"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center justify-between px-4 py-3 text-left text-[13px] font-semibold transition-colors duration-150 hover:bg-[var(--bg-secondary)]"
              style={{ color: 'var(--text-main)', backgroundColor: 'var(--bg-card)' }}
            >
              <span>{item.q}</span>
              <svg
                className="w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200"
                style={{ transform: open === i ? 'rotate(180deg)' : 'none', color: 'var(--text-muted)' }}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {open === i && (
              <div
                className="px-4 py-3 text-[13px] leading-relaxed border-t"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-body)' }}
              >
                {item.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * İçerik ARTIK AĞDAN ÇEKİLMİYOR. Eskiden useEffect ile var olmayan bir uca istek
 * atılıyordu; hem her sayfa açılışında 404 üretiyor hem de içerik ilk HTML'e hiç
 * girmediği için FAQ şeması indekslenemiyordu. Artık ilk render'da hazır.
 */
export default function InstrumentAbout({
  symbol,
  kind = 'stock',
}: {
  symbol: string
  kind?: InstrumentKind
}) {
  const lang = useLocale()
  const content = getInstrumentContent(symbol, kind, lang)

  if (!content || (!content.description && content.faq.length === 0)) return null

  const faqJsonLd = content.faq.length > 0
    ? faqSchema(content.faq.map(f => ({ q: f.q, a: f.a })))
    : null

  return (
    <>
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLd(faqJsonLd) }}
        />
      )}
    <section
      className="mt-8 rounded-xl border p-5"
      style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-card)' }}
    >
      <h2
        className="text-[16px] font-bold mb-3"
        style={{ color: 'var(--text-main)' }}
      >
        {t('instrument.aboutTitle', lang)} {symbol}
      </h2>
      {content.description && (
        <p
          className="text-[13px] leading-relaxed m-0"
          style={{ color: 'var(--text-muted)' }}
        >
          {content.description}
        </p>
      )}
      <FaqAccordion items={content.faq} lang={lang} />
    </section>
    </>
  )
}
